import { datadogRum } from '@datadog/browser-rum';
import { DividaResource } from '@/models/resources';
import useGlobalStore from '@/stores/global';

import queryStringService from './QueryStringService';

const themes = [
  'inter',
  'portal',
  'acerto',
] as const;

export type Theme = typeof themes[number];
const themesSet: ReadonlySet<string> = new Set(themes);

// == Variáveis de controle ===================================

let themeIsLocked = false;
let lastThemeApplied: Theme | undefined;

const cssStylesCache: Partial<Record<string, HTMLLinkElement>> = {};
const fallBackTheme = appSettings.VUE_APP_THEME;

// == Methods ===================================

function isTheme(txt: string): txt is Theme {
  return themesSet.has(txt);
}

function loadCss(href: string, cacheKey: string, onLoad?: () => void): void {
  const oldLinkEl = cssStylesCache[cacheKey];

  const newLinkEl = document.createElement('link');
  newLinkEl.rel = 'stylesheet';
  newLinkEl.type = 'text/css';
  newLinkEl.href = href;
  newLinkEl.media = 'all';
  newLinkEl.onload = () => {
    if (onLoad) onLoad();
    if (oldLinkEl) document.head.removeChild(oldLinkEl);
  };

  document.head.appendChild(newLinkEl);
  cssStylesCache[cacheKey] = newLinkEl;
}

function getCurrentTheme(): string {
  return localStorage.getItem('tema') || fallBackTheme;
}

function isInterTheme(): boolean {
  return getCurrentTheme() === 'inter' || fallBackTheme === 'inter';
}

function normalizeTheme(themeName?: string): Theme {
  const qs = queryStringService.getFromStorage();
  if (qs.utm_medium === 'card_negociacao' && qs.utm_source === 'app_inter') {
    // força tema quando vier do SuperApp do Inter - https://acerto.atlassian.net/browse/TES-10079
    return 'portal';
  }

  const finalTheme = themeName || getCurrentTheme();

  return isTheme(finalTheme) ? finalTheme : fallBackTheme as 'acerto' | 'inter' | 'portal';
}

function loadTheme(themeName?: string, lockTheme = false): void {
  datadogRum.addAction('sys:theme_service:load_theme', { themeName, lockTheme, themeIsLocked });

  if (themeIsLocked) {
    return;
  }
  const serasaThemeLocked = themeName === 'serasa';
  const isAppInter = appSettings.VUE_APP_NAME === 'inter';

  if (lockTheme || serasaThemeLocked || isAppInter) themeIsLocked = true;

  const finalTheme = isAppInter ? 'inter' : normalizeTheme(themeName);
  if (lastThemeApplied === finalTheme) return;

  lastThemeApplied = finalTheme;

  loadCss('/themes/default/style.css', 'default');
  loadCss(
    `/themes/${finalTheme}/style.css`,
    'theme',
    () => {
      const globalStore = useGlobalStore();
      globalStore.loadedTheme = finalTheme;
    },
  );

  localStorage.setItem('tema', finalTheme || 'portal');
}

function loadThemeByCredor(dividas: DividaResource[]): void {
  const credores = dividas.map((d) => d.credor);
  const isCredorUnico = new Set(credores).size === 1;

  if (!isCredorUnico || credores[0] !== lastThemeApplied) {
    loadTheme(fallBackTheme);
  }
}

function overrideAppBg(bg?: string): void {
  const bodyStyle = document.body.style;
  const cssVariable = '--acerto-app-bg-override';

  if (bg) {
    bodyStyle.setProperty(cssVariable, bg);
  } else {
    bodyStyle.removeProperty(cssVariable);
  }
}

// == Exports ===================================

export default {
  loadTheme,
  loadThemeByCredor,
  getCurrentTheme,
  isInterTheme,
  overrideAppBg,
  get fallbackTheme(): string {
    return fallBackTheme;
  },
};
